/**
 * Pure H OS · Phase D Wave 4 · Customer 360.
 * Aggregated customer view — segment, tier, lifetime, retention risk.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge, Button } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { CUSTOMERS_360, CustomerSegment, RetentionRisk } from "../mocks/customers360Data";
import { ORDERS } from "../mocks/commerceData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

function riskSeverity(r: RetentionRisk): "low" | "medium" | "high" {
  return r === "low" ? "low" : r === "medium" ? "medium" : "high";
}

type Tab = "all" | "high-risk" | CustomerSegment;

export default function Customer360() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [drawerTab, setDrawerTab] = useState<"overview" | "soa">("overview");
  const [soaCustomerId, setSoaCustomerId] = useState<string | null>(null);
  const [soaSummary, setSoaSummary] = useState<null | {
    currencies: string[];
    byCurrency: Record<string, { opening: number; debits: number; credits: number; closing: number; }>;
    aging: Record<string, { total: number; overdue: number; }>;
    loyalty: { currentTier: string; lifetimeSpendEGP: number; nextTier: string | null; gapToNextEGP: number; } | null;
    lastPaymentAt: string | null;
    unallocated: Record<string, number>;
    availableCredit: Record<string, number>;
    txnCount: number;
  }>(null);
  const [soaError, setSoaError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return CUSTOMERS_360;
    if (tab === "high-risk") return CUSTOMERS_360.filter((c) => c.retentionRisk === "high");
    return CUSTOMERS_360.filter((c) => c.segment === tab);
  }, [tab]);

  const current = openId ? CUSTOMERS_360.find((c) => c.id === openId) ?? null : null;

  // When the SOA tab opens, resolve the live customer_id via name and load the statement summary.
  useEffect(() => {
    if (!current || drawerTab !== "soa") return;
    setSoaError(null); setSoaSummary(null); setSoaCustomerId(null);
    fetch(`${API}/data/customers`, { credentials: "include" })
      .then((r) => r.json())
      .then((list: Array<{ id: string; name: string }>) => {
        const match = list.find((c) => c.name.toLowerCase() === current.name.toLowerCase());
        if (!match) { setSoaError(`No canonical customer record found for '${current.name}'.`); return; }
        setSoaCustomerId(match.id);
        return fetch(`${API}/customers/${match.id}/statement`, { credentials: "include" });
      })
      .then(async (r) => {
        if (!r) return;
        if (!r.ok) { setSoaError(`HTTP ${r.status}`); return; }
        const d = await r.json();
        setSoaSummary({
          currencies: d.currencies || [],
          byCurrency: d.summary?.byCurrency || {},
          aging: d.aging || {},
          loyalty: d.loyalty || null,
          lastPaymentAt: d.summary?.lastPaymentAt || null,
          unallocated: d.summary?.unallocated || {},
          availableCredit: d.summary?.availableCredit || {},
          txnCount: (d.transactions || []).length,
        });
      })
      .catch((e) => setSoaError(String(e)));
  }, [current, drawerTab]);

  return (
    <div className="space-y-6" data-testid="customer-360-page">
      <Card testId="c360-filters">
        <TabBar<Tab>
          testId="c360-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${CUSTOMERS_360.length}` },
            { key: "Retail",    label: "Retail" },
            { key: "Wholesale", label: "Wholesale" },
            { key: "high-risk", label: "High risk" },
          ]}
        />
      </Card>

      <Card padded={false} testId="c360-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Customer 360" title={`${filtered.length} customer${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Retail and wholesale · lifetime, NPS, retention risk.</div>
        </div>
        <div className="p-2">
          {filtered.map((c) => (
            <ListRow key={c.id} testId={`c360-${c.id}`}
              onClick={() => setOpenId(c.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{c.tier}</span>
                  <span>{c.name}</span>
                </span>
              }
              sublabel={<>{c.segment} · {c.lifetimeOrders} orders · EGP {c.lifetimeSpendEGP.toLocaleString()} LTV · NPS {c.nps} · last {c.lastOrderRelative}</>}
              trailing={<SeverityBadge severity={riskSeverity(c.retentionRisk)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => { setOpenId(null); setDrawerTab("overview"); }} testId="c360-drawer" title={current ? current.name : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${current.segment} · ${current.tier}`} title={current.name} />
            <TabBar<"overview" | "soa">
              testId="c360-drawer-tabs"
              active={drawerTab}
              onChange={setDrawerTab}
              tabs={[
                { key: "overview", label: "Overview" },
                { key: "soa",      label: "Statement of Account" },
              ]}
            />
            {drawerTab === "soa" ? (
              <div className="space-y-4" data-testid="c360-soa-panel">
                {soaError && <div className="text-sm" style={{ color: "var(--ph-danger)" }}>{soaError}</div>}
                {!soaError && !soaSummary && <div className="text-sm" style={{ color: "var(--ph-muted)" }}>Loading statement…</div>}
                {soaSummary && (
                  <>
                    {soaSummary.loyalty && (
                      <div className="rounded p-3 grid grid-cols-2 gap-2 text-xs" style={{ background: "var(--ph-warning-tint)", border: "1px solid var(--ph-warning)" }}>
                        <div><b>Tier:</b> {soaSummary.loyalty.currentTier}</div>
                        <div><b>Lifetime:</b> EGP {soaSummary.loyalty.lifetimeSpendEGP.toLocaleString()}</div>
                        <div><b>Next tier:</b> {soaSummary.loyalty.nextTier || "Top tier"}</div>
                        {soaSummary.loyalty.nextTier && <div><b>Gap:</b> EGP {soaSummary.loyalty.gapToNextEGP.toLocaleString()}</div>}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(soaSummary.byCurrency).map(([c, s]) => (
                        <div key={c} className="p-3 rounded" style={{ background: "var(--ph-surface-well)", border: "1px solid var(--ph-border-subtle)" }} data-testid={`c360-soa-ccy-${c}`}>
                          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ph-muted)" }}>{c} · closing</div>
                          <div className="text-lg tabular-nums" style={{ color: "var(--ph-text)" }}>{s.closing.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-[10px] mt-1" style={{ color: "var(--ph-muted)" }}>Debits {s.debits.toLocaleString()} · Credits {s.credits.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                    {Object.keys(soaSummary.aging).length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ph-muted)" }}>Aging</div>
                        {Object.entries(soaSummary.aging).map(([c, a]) => (
                          <div key={c} className="text-xs" data-testid={`c360-soa-aging-${c}`}>
                            {c}: total {a.total.toLocaleString()} · overdue {a.overdue.toLocaleString()}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
                      {soaSummary.txnCount} ledger transaction{soaSummary.txnCount === 1 ? "" : "s"} · last payment {soaSummary.lastPaymentAt || "—"}
                    </div>
                    {soaCustomerId && (
                      <div className="flex gap-2 pt-2" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                        <Button testId="c360-soa-open-full" onClick={() => window.open(`/pureh/finance/customer-statements`, "_blank")}>Open full workspace</Button>
                        <Button testId="c360-soa-download-pdf" onClick={() => window.location.assign(`${API}/customers/${soaCustomerId}/statement/pdf`)}>Download PDF</Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Lifetime orders</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.lifetimeOrders}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Lifetime spend</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.lifetimeSpendEGP.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Last order</div><div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.lastOrderRelative}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>NPS</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.nps}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Preferred channel</div><div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.preferredChannel}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Retention risk</div><div className="mt-2"><SeverityBadge severity={riskSeverity(current.retentionRisk)} /></div></div>
            </div>

            {/* Customer Order History — matched by customer name against live ORDERS collection */}
            {(() => {
              const orders = ORDERS.filter((o) => o.customer.toLowerCase() === current.name.toLowerCase());
              if (orders.length === 0) {
                return (
                  <div data-testid={`c360-orders-empty-${current.id}`}>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Order history</div>
                    <div className="mt-2 text-xs" style={{ color: "var(--ph-muted)" }}>No orders found matching this customer in the live Orders index.</div>
                  </div>
                );
              }
              return (
                <div data-testid={`c360-orders-${current.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Order history · {orders.length}</div>
                  <div className="mt-3 space-y-2">
                    {orders.map((o) => (
                      <div key={o.id} className="p-3 rounded text-sm" style={{ background: "var(--ph-surface-well)", border: "1px solid var(--ph-border-subtle)" }} data-testid={`c360-order-${o.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div style={{ color: "var(--ph-text)" }}>{o.reference}</div>
                            <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{o.channel} · {o.status} · {o.placedRelative} · {o.paymentMethod}</div>
                          </div>
                          <div className="text-right tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {o.totalEGP.toLocaleString()}</div>
                        </div>
                        <div className="mt-2 text-[10px]" style={{ color: "var(--ph-muted)" }}>
                          {o.lines.map((l) => `${l.qty}× ${l.name}`).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
